import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const API_BASE = 'https://api.pacificpearltraining.site';
const DEFAULT_RESTAURANT = 'Peking Chinese Restaurant';
const RESTAURANTS = [
  'Peking Chinese Restaurant',
  'China Wok',
  'Excel Chinese Food',
  'Great Wall',
  'To-Kai Sushi',
  'First Wok NY Style',
  "Top's China",
  "Top's China - Prince George",
  'No1 Wok Restaurant',
  'China Wang',
  'New China Restaurant',
  'Mei Wei Restaurant'
];

const Page = styled.div`
  height: 100vh;
  background: #f6f7f9;
  color: #1f2933;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 14px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  flex: 0 0 auto;

  @media (max-width: 900px) {
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.8rem;

  @media (max-width: 900px) {
    font-size: 1.45rem;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #cfd7df;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 260px;
  background: #fff;

  @media (max-width: 900px) {
    flex: 1 1 220px;
    min-width: 0;
  }
`;

const Button = styled.button`
  padding: 8px 12px;
  border: 1px solid ${props => props.$danger ? '#b42318' : '#1d4ed8'};
  border-radius: 6px;
  background: ${props => props.$secondary ? '#fff' : props.$danger ? '#b42318' : '#1d4ed8'};
  color: ${props => props.$secondary ? '#1d4ed8' : '#fff'};
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 38%) minmax(0, 1fr);
  gap: 16px;
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(240px, 42%) minmax(0, 1fr);
    gap: 10px;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(160px, 44%) minmax(0, 1fr);
    gap: 8px;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  overflow: auto;
  min-height: 0;
`;

const DetailPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ReviewRow = styled.button`
  width: 100%;
  display: block;
  padding: 12px 14px;
  border: 0;
  border-bottom: 1px solid #e5e9ef;
  background: ${props => props.$selected ? '#e8f1ff' : '#fff'};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f3f7fd;
  }

  @media (max-width: 640px) {
    padding: 10px 8px;
  }
`;

const RowTitle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-weight: 700;
  font-size: 0.95rem;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
    font-size: 0.82rem;
  }
`;

const RowMeta = styled.div`
  margin-top: 4px;
  color: #667085;
  font-size: 0.8rem;

  @media (max-width: 640px) {
    font-size: 0.72rem;
  }
`;

const Status = styled.span`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${props => props.$invalid ? '#fee4e2' : props.$done ? '#dcfce7' : '#fff7cc'};
  color: ${props => props.$invalid ? '#b42318' : props.$done ? '#166534' : '#854d0e'};
  font-size: 0.75rem;
  white-space: nowrap;
`;

const Detail = styled.div`
  padding: 16px;

  @media (max-width: 640px) {
    padding: 10px;
  }
`;

const DetailBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
`;

const SectionTitle = styled.h2`
  margin: 18px 0 8px;
  font-size: 1rem;
`;

const TextBlock = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
  font: inherit;
  font-size: 0.85rem;
  line-height: 1.45;
`;

const NoteCheckBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0 14px;
  padding: 8px 10px;
  border: 1px solid ${props => props.$invalid ? '#fecaca' : '#e5e9ef'};
  border-radius: 6px;
  background: ${props => props.$invalid ? '#fff7f7' : '#f8fafc'};
  color: #475467;
  font-size: 0.78rem;
  line-height: 1.35;
`;

const NoteCheckLabel = styled.span`
  flex: 0 0 auto;
  color: #667085;
  font-weight: 700;
`;

const NoteCheckText = styled.span`
  min-width: 0;
  word-break: break-word;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  ${Button} {
    min-height: 42px;
    padding: 10px 16px;
  }

  @media (max-width: 760px) {
    ${Button} {
      flex: 1 1 170px;
    }
  }
`;

const ActionFooter = styled.div`
  flex: 0 0 auto;
  padding: 14px 16px;
  border-top: 1px solid #d8dee6;
  background: #fff;
  box-shadow: 0 -8px 18px rgba(15, 23, 42, 0.08);

  @media (max-width: 640px) {
    padding: 10px;
  }
`;

const ActionStatus = styled(RowMeta)`
  margin-top: 8px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin: 14px 0 4px;
`;

const StatBox = styled.div`
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
`;

const StatLabel = styled.div`
  color: #667085;
  font-size: 0.75rem;
`;

const StatValue = styled.div`
  margin-top: 3px;
  font-size: 1rem;
  font-weight: 700;
`;

const OrderGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const ComparisonHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) 108px;
  gap: 8px;
  align-items: stretch;
  min-width: 500px;
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) 108px;
  gap: 8px;
  align-items: stretch;
  min-width: 500px;
`;

const HeaderCell = styled.div`
  padding: 10px 12px;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
  background: ${props => props.$real ? '#ecfdf3' : '#eff6ff'};
  font-weight: 700;
  text-align: center;
`;

const MatchBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  border-radius: 8px;
  border: 1px solid ${props => {
    if (props.$status === 'match') return '#86efac';
    if (props.$status === 'close') return '#facc15';
    return '#fca5a5';
  }};
  background: ${props => {
    if (props.$status === 'match') return '#f0fdf4';
    if (props.$status === 'close') return '#fffbeb';
    return '#fef2f2';
  }};
  color: ${props => {
    if (props.$status === 'match') return '#166534';
    if (props.$status === 'close') return '#854d0e';
    return '#b42318';
  }};
  font-size: 0.78rem;
  font-weight: 700;
  text-align: center;
  white-space: pre-line;
  padding: 8px;
`;

const OrderMeta = styled.div`
  padding: 8px 12px;
  color: #667085;
  font-size: 0.78rem;
  line-height: 1.4;
  border-bottom: 1px solid #eef2f6;
`;

const ItemCard = styled.div`
  padding: 10px 12px;
  min-height: 82px;
  border: 1px solid ${props => {
    if (props.$status === 'match') return '#bbf7d0';
    if (props.$status === 'close') return '#fde68a';
    if (props.$status === 'missing' || props.$status === 'extra' || props.$status === 'mismatch') return '#fecaca';
    return '#eef2f6';
  }};
  background: ${props => {
    if (props.$status === 'match') return '#f0fdf4';
    if (props.$status === 'close') return '#fffbeb';
    if (props.$status === 'missing' || props.$status === 'extra' || props.$status === 'mismatch') return '#fff7f7';
    return '#fff';
  }};
  border-radius: 8px;
  box-sizing: border-box;
`;

const ItemTitle = styled.div`
  font-weight: 700;
  font-size: 0.86rem;
`;

const ItemMeta = styled.div`
  color: #667085;
  font-size: 0.78rem;
  margin-top: 3px;
`;

const PropertyList = styled.div`
  margin-top: 5px;
  color: #344054;
  font-size: 0.78rem;
  line-height: 1.35;
`;

const OptionInsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin: 10px 0;
`;

const OptionInsightCard = styled.div`
  padding: 10px 12px;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
  background: #fff;
`;

const OptionInsightTitle = styled.div`
  font-weight: 700;
  font-size: 0.86rem;
`;

const OptionInsightMeta = styled.div`
  margin-top: 4px;
  color: #667085;
  font-size: 0.78rem;
`;

const ReadinessPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
  gap: 10px;
  margin: 10px 0;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ReadinessScore = styled.div`
  padding: 12px;
  border: 1px solid ${props => props.$score === null ? '#d8dee6' : props.$score >= 85 ? '#86efac' : props.$score >= 75 ? '#fde68a' : '#fecaca'};
  border-radius: 6px;
  background: ${props => props.$score === null ? '#f8fafc' : props.$score >= 85 ? '#f0fdf4' : props.$score >= 75 ? '#fffbeb' : '#fff7f7'};
`;

const ReadinessStatus = styled.div`
  font-size: 1rem;
  font-weight: 800;
`;

const ReadinessNumber = styled.div`
  margin-top: 4px;
  font-size: 1.6rem;
  font-weight: 800;
`;

const CoachList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CoachItem = styled.div`
  padding: 8px 10px;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
  background: #fff;
`;

const CoachTitle = styled.div`
  font-weight: 700;
  font-size: 0.84rem;
`;

const CoachMeta = styled.div`
  margin-top: 3px;
  color: #667085;
  font-size: 0.76rem;
  line-height: 1.35;
`;

const SkillRows = styled.div`
  display: grid;
  grid-template-columns: minmax(130px, 1.2fr) repeat(4, minmax(68px, 0.55fr));
  gap: 6px;
  min-width: 560px;
`;

const SkillCell = styled.div`
  padding: 7px 8px;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
  background: ${props => props.$header ? '#f8fafc' : '#fff'};
  font-size: 0.76rem;
  font-weight: ${props => props.$header ? 700 : 500};
`;

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString();
}

function formatShortDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString();
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : String(value);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return 'n/a';
  const number = Number(value);
  return Number.isFinite(number) ? `${Math.round(number)}%` : String(value);
}

function formatAddress(address) {
  if (!address) return '';
  return [
    address.name,
    [address.address1, address.address2].filter(Boolean).join(' '),
    [address.city, address.province, address.zip].filter(Boolean).join(', '),
    address.phone
  ].filter(Boolean).join('\n');
}

function getStatusFlags(review) {
  const status = review?.status || '';
  return {
    invalid: status.includes('invalid'),
    done: status.includes('reviewed') || status.includes('resolved')
  };
}

function isPassedReview(review) {
  return review?.status === 'training_order_passed';
}

function summarizeItem(item) {
  if (!item) return '';
  const parts = [
    item.quantity ? `${item.quantity}x` : null,
    item.title,
    item.variantTitle ? `(${item.variantTitle})` : null,
    item.sku ? `[${item.sku}]` : null
  ].filter(Boolean);
  return parts.join(' ');
}

function normalizeCompareText(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function normalizePrice(value) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : normalizeCompareText(value);
}

function normalizeProperties(properties = []) {
  return properties
    .map(property => `${normalizeCompareText(property.name)}:${normalizeCompareText(property.value)}`)
    .sort()
    .join('|');
}

function scorePair(realItem, traineeItem) {
  const realSku = normalizeCompareText(realItem?.sku);
  const traineeSku = normalizeCompareText(traineeItem?.sku);
  const realTitle = normalizeCompareText(realItem?.title);
  const traineeTitle = normalizeCompareText(traineeItem?.title);
  const realVariant = normalizeCompareText(realItem?.variantTitle);
  const traineeVariant = normalizeCompareText(traineeItem?.variantTitle);
  const realProps = normalizeProperties(realItem?.properties || []);
  const traineeProps = normalizeProperties(traineeItem?.properties || []);
  const sameSku = Boolean(realSku && traineeSku && realSku === traineeSku);
  const sameTitle = Boolean(realTitle && traineeTitle && realTitle === traineeTitle);

  if (!sameSku && !sameTitle) return 0;

  let score = 0;
  if (sameSku) score += 60;
  if (sameTitle) score += 30;
  if (String(realItem?.quantity ?? '') === String(traineeItem?.quantity ?? '')) score += 15;
  if (realVariant === traineeVariant) score += 8;
  if (realProps === traineeProps) score += 12;
  if (normalizePrice(realItem?.price) === normalizePrice(traineeItem?.price)) score += 5;
  return score;
}

function getPairStatus(realItem, traineeItem) {
  if (realItem && !traineeItem) return 'missing';
  if (!realItem && traineeItem) return 'extra';
  if (!realItem || !traineeItem) return 'mismatch';

  const sameSku = normalizeCompareText(realItem.sku) === normalizeCompareText(traineeItem.sku);
  const sameTitle = normalizeCompareText(realItem.title) === normalizeCompareText(traineeItem.title);
  const sameQuantity = String(realItem.quantity ?? '') === String(traineeItem.quantity ?? '');
  const sameVariant = normalizeCompareText(realItem.variantTitle) === normalizeCompareText(traineeItem.variantTitle);
  const sameProperties = normalizeProperties(realItem.properties || []) === normalizeProperties(traineeItem.properties || []);

  if ((sameSku || sameTitle) && sameQuantity && sameVariant && sameProperties) return 'match';
  return 'close';
}

function getStatusLabel(status, realItem, traineeItem) {
  if (status === 'match') {
    const priceDiffers = normalizePrice(realItem?.price) !== normalizePrice(traineeItem?.price);
    return priceDiffers ? 'Matched\nprice differs' : 'Matched';
  }
  if (status === 'close') return 'Review';
  if (status === 'missing') return 'Missing\nfrom trainee';
  if (status === 'extra') return 'Extra\nfrom trainee';
  return 'Mismatch';
}

function pairOrderItems(realItems = [], traineeItems = []) {
  const unusedTrainee = traineeItems.map((item, index) => ({ item, index }));
  const rows = [];

  realItems.forEach((realItem, realIndex) => {
    let bestMatch = null;
    unusedTrainee.forEach(candidate => {
      const score = scorePair(realItem, candidate.item);
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { ...candidate, score };
      }
    });

    if (bestMatch && bestMatch.score >= 60) {
      const traineeIndex = unusedTrainee.findIndex(candidate => candidate.index === bestMatch.index);
      if (traineeIndex >= 0) unusedTrainee.splice(traineeIndex, 1);
      const status = getPairStatus(realItem, bestMatch.item);
      rows.push({
        key: `real-${realIndex}-trainee-${bestMatch.index}`,
        realItem,
        traineeItem: bestMatch.item,
        status
      });
    } else {
      rows.push({
        key: `real-${realIndex}-missing`,
        realItem,
        traineeItem: null,
        status: 'missing'
      });
    }
  });

  unusedTrainee.forEach(candidate => {
    rows.push({
      key: `extra-trainee-${candidate.index}`,
      realItem: null,
      traineeItem: candidate.item,
      status: 'extra'
    });
  });

  return rows;
}

function renderComparisonItem(item, status, emptyText) {
  if (!item) {
    return (
      <ItemCard $status={status}>
        <ItemTitle>{emptyText}</ItemTitle>
      </ItemCard>
    );
  }

  return (
    <ItemCard $status={status}>
      <ItemTitle>{summarizeItem(item)}</ItemTitle>
      <ItemMeta>
        {[item.price ? formatMoney(item.price) : null, item.issueType ? item.issueType.replaceAll('_', ' ') : null]
          .filter(Boolean)
          .join(' / ')}
      </ItemMeta>
      {item.properties?.length > 0 && (
        <PropertyList>
          {item.properties.map((property, propertyIndex) => (
            <div key={`${property.name}-${propertyIndex}`}>
              {property.name}: {property.value}
            </div>
          ))}
        </PropertyList>
      )}
    </ItemCard>
  );
}

function getInitialRestaurant() {
  const params = new URLSearchParams(window.location.search);
  const restaurantFromUrl = params.get('restaurant');
  if (restaurantFromUrl && RESTAURANTS.includes(restaurantFromUrl)) return restaurantFromUrl;
  return DEFAULT_RESTAURANT;
}

function getInitialTraineeId() {
  const params = new URLSearchParams(window.location.search);
  return params.get('traineeId') || '';
}

export default function TrainerReviews() {
  const [restaurant, setRestaurant] = useState(getInitialRestaurant);
  const [trainees, setTrainees] = useState([]);
  const [traineeId, setTraineeId] = useState(getInitialTraineeId);
  const [reviews, setReviews] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [traineeLoading, setTraineeLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailsById, setDetailsById] = useState({});
  const [optionSummary, setOptionSummary] = useState(null);
  const [optionSummaryLoading, setOptionSummaryLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [regeneratingAudio, setRegeneratingAudio] = useState(false);

  const selectedReview = useMemo(
    () => reviews.find(review => review.id === selectedId) || null,
    [reviews, selectedId]
  );
  const selectedTrainee = useMemo(
    () => trainees.find(trainee => trainee.customerId === traineeId) || null,
    [trainees, traineeId]
  );
  const selectedDetails = selectedReview ? detailsById[selectedReview.id] : null;

  const loadTrainees = async () => {
    setTraineeLoading(true);
    setError('');
    setReviews([]);
    setDetailsById({});
    setSelectedId(null);
    try {
      const params = new URLSearchParams({ restaurant });
      const response = await fetch(`${API_BASE}/training-reviews/trainees?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to load trainees.');
      const nextTrainees = payload.trainees || [];
      setTrainees(nextTrainees);
      setTraineeId(current => (
        current && nextTrainees.some(trainee => trainee.customerId === current)
          ? current
          : ''
      ));
    } catch (err) {
      setTrainees([]);
      setTraineeId('');
      setError(err.message);
    } finally {
      setTraineeLoading(false);
    }
  };

  const loadReviews = async () => {
    if (!traineeId) {
      setReviews([]);
      setDetailsById({});
      setSelectedId(null);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        restaurant,
        traineeId,
        maxOrders: '200',
        includePassed: 'false'
      });
      const response = await fetch(`${API_BASE}/training-reviews?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to load reviews.');
      const visibleReviews = (payload.reviews || []).filter(review => !isPassedReview(review));
      setReviews(visibleReviews);
      setDetailsById({});
      setSelectedId(null);
      window.history.replaceState(
        null,
        '',
        `/trainer/reviews?restaurant=${encodeURIComponent(restaurant)}&traineeId=${encodeURIComponent(traineeId)}`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrainees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant]);

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant, traineeId]);

  useEffect(() => {
    if (!traineeId || !selectedReview) {
      setOptionSummary(null);
      return;
    }

    const controller = new AbortController();
    const loadOptionSummary = async () => {
      setOptionSummaryLoading(true);
      try {
        const params = new URLSearchParams({
          traineeId,
          restaurantName: restaurant,
          days: '30'
        });
        const response = await fetch(
          `${API_BASE}/training-option-speed-summary?${params.toString()}`,
          { signal: controller.signal }
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Failed to load option speed summary.');
        setOptionSummary(payload);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setOptionSummary({ error: err.message, slowSelections: [], failedQueries: [] });
        }
      } finally {
        if (!controller.signal.aborted) setOptionSummaryLoading(false);
      }
    };

    loadOptionSummary();
    return () => controller.abort();
  }, [restaurant, traineeId, selectedReview]);

  useEffect(() => {
    if (!selectedReview?.traineeOrderId || detailsById[selectedReview.id]) return;

    const controller = new AbortController();
    const loadDetails = async () => {
      setDetailLoading(true);
      try {
        const params = new URLSearchParams({ reviewId: selectedReview.id });
        const response = await fetch(
          `${API_BASE}/training-reviews/${selectedReview.traineeOrderId}/details?${params.toString()}`,
          { signal: controller.signal }
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Failed to load order details.');
        setDetailsById(current => ({ ...current, [selectedReview.id]: payload }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          setDetailsById(current => ({
            ...current,
            [selectedReview.id]: { error: err.message }
          }));
        }
      } finally {
        if (!controller.signal.aborted) setDetailLoading(false);
      }
    };

    loadDetails();
    return () => controller.abort();
  }, [selectedReview?.id, selectedReview?.traineeOrderId, detailsById, selectedReview]);

  const submitTrainerDecision = async (status, decision) => {
    if (!selectedReview) return;
    setActionStatus('Saving...');
    try {
      const response = await fetch(`${API_BASE}/training-reviews/${selectedReview.traineeOrderId}/${selectedReview.id}/trainer-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, decision, trainerName: 'Trainer' })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Could not save trainer decision.');
      setActionStatus('Saved.');
      await loadReviews();
    } catch (err) {
      setActionStatus(err.message);
    }
  };

  const regenerateScriptAndAudio = async () => {
    if (!selectedReview) return;
    setRegeneratingAudio(true);
    setActionStatus('Recreating script and audio...');
    try {
      const response = await fetch(`${API_BASE}/training-reviews/${selectedReview.traineeOrderId}/regenerate-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewId: selectedReview.id,
          originalOrderId: selectedReview.originalOrderId || selectedReview.comparisonSummary?.originalOrderId
        })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Could not recreate script and audio.');

      setDetailsById(current => ({
        ...current,
        [selectedReview.id]: {
          ...(current[selectedReview.id] || {}),
          savedTranscript: payload.transcript || '',
          regeneratedAudio: payload
        }
      }));
      setReviews(current => current.map(review => (
        review.id === selectedReview.id
          ? { ...review, savedTranscript: payload.transcript || review.savedTranscript || '' }
          : review
      )));
      setActionStatus(`Recreated script and audio for real order ${payload.originalOrderName || payload.originalOrderId}.`);
    } catch (err) {
      setActionStatus(err.message);
    } finally {
      setRegeneratingAudio(false);
    }
  };

  const detailError = selectedDetails?.error || '';
  const comparisonSummary = selectedDetails?.comparisonSummary || selectedReview?.comparisonSummary || {};
  const trainingData = selectedDetails?.trainingData || selectedReview?.trainingData || {};
  const orderDetails = selectedDetails?.orderDetails || selectedReview?.orderDetails || {};
  const savedTranscript = selectedDetails?.savedTranscript || selectedReview?.savedTranscript || '';
  const statusFlags = getStatusFlags(selectedReview);
  const pairedOrderRows = pairOrderItems(
    orderDetails.original?.lineItems || [],
    orderDetails.trainee?.lineItems || []
  );

  return (
    <Page>
      <Header>
        <div>
          <Title>Trainer Reviews</Title>
          <div>
            {reviews.length} training job{reviews.length === 1 ? '' : 's'} need review
            {selectedTrainee ? ` for ${selectedTrainee.name}` : ''}
          </div>
        </div>
        <FilterBar>
          <Select
            value={restaurant}
            onChange={event => {
              setRestaurant(event.target.value);
              setTraineeId('');
            }}
          >
            {RESTAURANTS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Select>
          <Select
            value={traineeId}
            onChange={event => setTraineeId(event.target.value)}
            disabled={traineeLoading || trainees.length === 0}
          >
            {traineeLoading && <option value="">Loading trainees...</option>}
            {!traineeLoading && trainees.length === 0 && <option value="">No trainees found</option>}
            {!traineeLoading && trainees.length > 0 && <option value="">Select trainee</option>}
            {!traineeLoading && trainees.map(trainee => (
              <option key={trainee.customerId} value={trainee.customerId}>
                {trainee.name}{trainee.email ? ` - ${trainee.email}` : ''}
                {trainee.lastTrainingAt ? ` - last ${formatShortDate(trainee.lastTrainingAt)}` : ''}
              </option>
            ))}
          </Select>
          <Button onClick={loadReviews} disabled={loading || traineeLoading || !traineeId}>{loading ? 'Loading...' : 'Refresh'}</Button>
        </FilterBar>
      </Header>

      {error && <TextBlock>{error}</TextBlock>}

      <Layout>
        <Panel>
          {reviews.map(review => {
            const flags = getStatusFlags(review);
            const traineeName = `${review.trainee?.firstName || review.traineeInfo?.firstName || ''} ${review.trainee?.lastName || review.traineeInfo?.lastName || ''}`.trim();
            return (
              <ReviewRow key={review.id} $selected={selectedReview?.id === review.id} onClick={() => setSelectedId(review.id)}>
                <RowTitle>
                  <span>{traineeName || 'Unknown trainee'}</span>
                  <Status $invalid={flags.invalid} $done={flags.done}>{review.status || 'pending'}</Status>
                </RowTitle>
                <RowMeta>{review.issueLabel || review.issueType || 'No issue type'} - Order {review.traineeOrderName || review.traineeOrderId}</RowMeta>
                <RowMeta>{formatDate(review.createdAt)}</RowMeta>
              </ReviewRow>
            );
          })}
          {!loading && traineeId && reviews.length === 0 && <Detail>No training jobs need review for this trainee.</Detail>}
          {!loading && !traineeId && <Detail>Select a trainee.</Detail>}
          {!loading && traineeId && reviews.length > 0 && !selectedReview && <Detail>Select an order to load the full details.</Detail>}
        </Panel>

        <DetailPanel>
          {selectedReview ? (
            <>
              <DetailBody>
                <Detail>
                  <RowTitle>
                    <span>{selectedReview.issueLabel || selectedReview.issueType}</span>
                    <Status $invalid={statusFlags.invalid} $done={statusFlags.done}>{selectedReview.status}</Status>
                  </RowTitle>
                  <RowMeta>
                    Trainee order {selectedReview.traineeOrderName || selectedReview.traineeOrderId}
                    {' '}vs real order {selectedReview.originalOrderName || selectedReview.originalOrderId}
                  </RowMeta>

                  <StatGrid>
                    <StatBox>
                      <StatLabel>Score</StatLabel>
                      <StatValue>{comparisonSummary.score ?? trainingData?.score ?? 'n/a'}</StatValue>
                    </StatBox>
                    <StatBox>
                      <StatLabel>Unmatched Penalty</StatLabel>
                      <StatValue>{comparisonSummary.unmatchedPenalty ?? trainingData?.unmatchedPenalty ?? 'n/a'}</StatValue>
                    </StatBox>
                    <StatBox>
                      <StatLabel>Time Penalty</StatLabel>
                      <StatValue>{comparisonSummary.timePenalty ?? trainingData?.timePenalty ?? 'n/a'}</StatValue>
                    </StatBox>
                    <StatBox>
                      <StatLabel>Items</StatLabel>
                      <StatValue>
                        {(comparisonSummary.traineeItemCount ?? trainingData?.totalItemsInTraineeOrder ?? '?')}
                        {' / '}
                        {(comparisonSummary.originalItemCount ?? trainingData?.totalItemsInRealOrder ?? '?')}
                      </StatValue>
                    </StatBox>
                  </StatGrid>
                  <SectionTitle>Training Patterns</SectionTitle>
                  {optionSummaryLoading && <RowMeta>Loading training patterns...</RowMeta>}
                  {optionSummary?.error && <TextBlock>{optionSummary.error}</TextBlock>}
                  {!optionSummaryLoading && !optionSummary?.error && (
                    <>
                      <RowMeta>
                        Last 30 days / {optionSummary?.eventCount || 0} search event{optionSummary?.eventCount === 1 ? '' : 's'}
                      </RowMeta>
                      {optionSummary?.readiness && (
                        <ReadinessPanel>
                          <ReadinessScore $score={optionSummary.readiness.score}>
                            <ReadinessStatus>{optionSummary.readiness.status}</ReadinessStatus>
                            <ReadinessNumber>
                              {optionSummary.readiness.score === null ? 'Needs data' : optionSummary.readiness.score}
                            </ReadinessNumber>
                            <CoachMeta>{optionSummary.readiness.explanation}</CoachMeta>
                          </ReadinessScore>
                          <CoachList>
                            <CoachItem>
                              <CoachTitle>Readiness signals</CoachTitle>
                              <CoachMeta>
                                Accuracy {formatPercent(optionSummary.readiness.accuracy)}
                                {' / '}search success {formatPercent(optionSummary.readiness.searchSuccess)}
                                {' / '}independence {formatPercent(optionSummary.readiness.independence)}
                              </CoachMeta>
                              {optionSummary.readiness.gates?.length > 0 && (
                                <CoachMeta>{optionSummary.readiness.gates.join(' ')}</CoachMeta>
                              )}
                            </CoachItem>
                            {(optionSummary.recommendedDrills || []).slice(0, 3).map(drill => (
                              <CoachItem key={`${drill.label}-${drill.reason}`}>
                                <CoachTitle>{drill.label}</CoachTitle>
                                <CoachMeta>{drill.reason}</CoachMeta>
                              </CoachItem>
                            ))}
                          </CoachList>
                        </ReadinessPanel>
                      )}
                      {(optionSummary?.coachablePatterns || []).length > 0 && (
                        <>
                          <SectionTitle>Coachable Patterns</SectionTitle>
                          <OptionInsightGrid>
                            {(optionSummary.coachablePatterns || []).slice(0, 6).map(pattern => (
                              <OptionInsightCard key={pattern.key}>
                                <OptionInsightTitle>{pattern.title}</OptionInsightTitle>
                                <OptionInsightMeta>
                                  {pattern.count} signal{pattern.count === 1 ? '' : 's'}
                                  {' / '}{pattern.skill}
                                  {' / '}{pattern.recommendedAction}
                                </OptionInsightMeta>
                                {pattern.evidence?.[0] && <OptionInsightMeta>{pattern.evidence[0]}</OptionInsightMeta>}
                              </OptionInsightCard>
                            ))}
                          </OptionInsightGrid>
                        </>
                      )}
                      {(optionSummary?.skillBreakdown || []).length > 0 && (
                        <>
                          <SectionTitle>Skill Breakdown</SectionTitle>
                          <OrderGrid>
                            <SkillRows>
                              <SkillCell $header>Skill</SkillCell>
                              <SkillCell $header>Accuracy</SkillCell>
                              <SkillCell $header>Corrections</SkillCell>
                              <SkillCell $header>No results</SkillCell>
                              <SkillCell $header>Avg time</SkillCell>
                              {(optionSummary.skillBreakdown || []).slice(0, 8).map(skill => (
                                <React.Fragment key={skill.skill}>
                                  <SkillCell>{skill.skill}</SkillCell>
                                  <SkillCell>{formatPercent(skill.accuracy)}</SkillCell>
                                  <SkillCell>{skill.corrections || 0}</SkillCell>
                                  <SkillCell>{skill.zeroResults || 0}</SkillCell>
                                  <SkillCell>{skill.averageElapsedMs ? `${(skill.averageElapsedMs / 1000).toFixed(1)}s` : 'n/a'}</SkillCell>
                                </React.Fragment>
                              ))}
                            </SkillRows>
                          </OrderGrid>
                        </>
                      )}
                      {(optionSummary?.synonymQueue || []).length > 0 && (
                        <>
                          <SectionTitle>Synonym Queue</SectionTitle>
                          <OptionInsightGrid>
                            {(optionSummary.synonymQueue || []).slice(0, 4).map(item => (
                              <OptionInsightCard key={`${item.searchKind}-${item.failedQuery}-${item.intendedItem}`}>
                                <OptionInsightTitle>{item.failedQuery}</OptionInsightTitle>
                                <OptionInsightMeta>
                                  {item.frequency} failed search{item.frequency === 1 ? '' : 'es'}
                                  {item.intendedItem ? ` / likely ${item.intendedItem}` : ''}
                                </OptionInsightMeta>
                                <OptionInsightMeta>{item.action}</OptionInsightMeta>
                              </OptionInsightCard>
                            ))}
                          </OptionInsightGrid>
                        </>
                      )}
                      <SectionTitle>Raw Timing Signals</SectionTitle>
                      <OptionInsightGrid>
                        {(optionSummary?.slowSelections || []).slice(0, 6).map(item => (
                          <OptionInsightCard key={item.label}>
                            <OptionInsightTitle>{item.label}</OptionInsightTitle>
                            <OptionInsightMeta>
                              Avg {(item.averageElapsedMs / 1000).toFixed(1)}s / slowest {(item.maxElapsedMs / 1000).toFixed(1)}s / {item.count} select{item.count === 1 ? '' : 's'}
                            </OptionInsightMeta>
                            {item.examples?.[0]?.query && (
                              <OptionInsightMeta>Search: {item.examples[0].query}</OptionInsightMeta>
                            )}
                          </OptionInsightCard>
                        ))}
                        {(optionSummary?.failedQueries || []).slice(0, 4).map(item => (
                          <OptionInsightCard key={`${item.searchKind}-${item.query}`}>
                            <OptionInsightTitle>No results: {item.query}</OptionInsightTitle>
                            <OptionInsightMeta>
                              {item.count} failed {item.searchKind} search{item.count === 1 ? '' : 'es'}
                            </OptionInsightMeta>
                          </OptionInsightCard>
                        ))}
                      </OptionInsightGrid>
                      {(!optionSummary?.slowSelections?.length && !optionSummary?.failedQueries?.length) && (
                        <RowMeta>No option-search timing yet for this trainee.</RowMeta>
                      )}
                    </>
                  )}
                  {detailLoading && <RowMeta>Loading order details...</RowMeta>}
                  {detailError && <TextBlock>{detailError}</TextBlock>}

                  <SectionTitle>Trainee Note</SectionTitle>
                  <TextBlock>{selectedReview.reviewNote || '(empty)'}</TextBlock>

                  <NoteCheckBlock $invalid={selectedReview.noteValidation?.valid === false}>
                    <NoteCheckLabel>Note check</NoteCheckLabel>
                    <NoteCheckText>
                      {selectedReview.noteValidation?.feedbackToTrainee || selectedReview.noteValidation?.reason || '(not checked)'}
                    </NoteCheckText>
                  </NoteCheckBlock>

                  <SectionTitle>Transcript / Script</SectionTitle>
                  <TextBlock>{savedTranscript || '(not available)'}</TextBlock>

                  <SectionTitle>Order Items</SectionTitle>
                  <OrderGrid>
                    <ComparisonHeader>
                      <HeaderCell>
                        Trainee Order {orderDetails.trainee?.name || selectedReview.traineeOrderName || ''}
                        <RowMeta>
                          Total: {formatMoney(orderDetails.trainee?.totalPrice) || 'n/a'}
                          {orderDetails.trainee?.lineItems?.length ? ` / ${orderDetails.trainee.lineItems.length} item rows` : ''}
                        </RowMeta>
                      </HeaderCell>
                      <HeaderCell $real>
                        Real Order {orderDetails.original?.name || selectedReview.originalOrderName || ''}
                        <RowMeta>
                          Total: {formatMoney(orderDetails.original?.totalPrice) || 'n/a'}
                          {orderDetails.original?.lineItems?.length ? ` / ${orderDetails.original.lineItems.length} item rows` : ''}
                        </RowMeta>
                      </HeaderCell>
                      <HeaderCell>Status</HeaderCell>
                    </ComparisonHeader>
                    {formatAddress(orderDetails.original?.shippingAddress) && (
                      <OrderMeta>{formatAddress(orderDetails.original.shippingAddress)}</OrderMeta>
                    )}
                    {pairedOrderRows.map(row => (
                      <ComparisonRow key={row.key}>
                        {renderComparisonItem(row.traineeItem, row.status, 'No trainee line matched')}
                        {renderComparisonItem(row.realItem, row.status, 'Extra trainee line')}
                        <MatchBadge $status={row.status}>
                          {getStatusLabel(row.status, row.realItem, row.traineeItem)}
                        </MatchBadge>
                      </ComparisonRow>
                    ))}
                    {!pairedOrderRows.length && (
                      <ComparisonRow>
                        <ItemCard>{detailLoading ? 'Loading trainee order items...' : 'No trainee order items available.'}</ItemCard>
                        <ItemCard>{detailLoading ? 'Loading real order items...' : 'No real order items available.'}</ItemCard>
                        <MatchBadge $status="mismatch">No Data</MatchBadge>
                      </ComparisonRow>
                    )}
                  </OrderGrid>
                </Detail>
              </DetailBody>

              <ActionFooter>
                <ActionBar>
                  <Button
                    $secondary
                    onClick={regenerateScriptAndAudio}
                    disabled={regeneratingAudio || !(selectedReview.originalOrderId || selectedReview.comparisonSummary?.originalOrderId)}
                  >
                    {regeneratingAudio ? 'Recreating...' : 'Recreate Script + Audio'}
                  </Button>
                  <Button onClick={() => submitTrainerDecision('trainer_confirmed_system_issue', 'system_issue')}>
                    Confirm System Issue
                  </Button>
                  <Button $secondary onClick={() => submitTrainerDecision('trainer_confirmed_trainee_error', 'trainee_error')}>
                    Mark Trainee Error
                  </Button>
                  <Button $danger onClick={() => submitTrainerDecision('trainer_rejected_invalid_note', 'invalid_note')}>
                    Reject Note
                  </Button>
                </ActionBar>
                {actionStatus && <ActionStatus>{actionStatus}</ActionStatus>}
              </ActionFooter>
            </>
          ) : (
            <DetailBody>
              <Detail>{selectedTrainee ? 'Select an order to load full details.' : 'Select a trainee.'}</Detail>
            </DetailBody>
          )}
        </DetailPanel>
      </Layout>
    </Page>
  );
}
